import { useCrew } from '@/context/CrewProvider';
import { trpc } from '@/trpc/react';
import { zodResolver } from '@hookform/resolvers/zod';
import Drawer from 'expo-router/drawer';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { z } from 'zod';
import { router } from "expo-router";
import Toast from "react-native-toast-message";
import { addAvailabilityFormSchema, mapCreateCrewAvailabilityForBackend, resolveAvailabilityRange, validateAvailabilityRange } from '@/components/calendar/add-availability-helper';
import AvailabilityForm from '@/components/calendar/availability-form';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { AvailabilityClashAlert } from '@/components/calendar/availability-clash-alert';
import { showAvailabilityError } from '@/components/calendar/availability-toast';
import { busyDayKeys } from '@/components/calendar/availability-colors';
import colors from '@/constants/Colors';

export default function AddAvailability() {
  const [openClashAlert, setOpenClashAlert] = useState(false);

  const { currentCrewMember } = useCrew();
  const { data: contractTypeHistory } = trpc.crew.listContractTypeHistory.useQuery(Number(currentCrewMember?.id));

  const form = useForm<z.infer<typeof addAvailabilityFormSchema>>({
    resolver: zodResolver(addAvailabilityFormSchema),
    defaultValues: {}
  });

  // find latest contract type where effective date has past
  const latestContractType = contractTypeHistory?.find((contractType) => contractType.date <= new Date())?.type_id;
  const isEmployee = latestContractType ? latestContractType === 1 : currentCrewMember?.contract_type === 1;
  const isAvailability = !currentCrewMember?.is_full_time;


  const { data: crewJobDates } = trpc.jobDateListRouter.listAllCrewJobs.useQuery(Number(currentCrewMember?.id), { enabled: !isEmployee })
  const mappedDates = crewJobDates ? crewJobDates?.data?.filter(date => date.crew_state === 2).map(date => date.date_started_at as Date) : [];

  // Shown as dots on the date picker. The save is refused over these days, so picking them has
  // to be visibly a bad idea rather than something you only discover on save.
  const { data: crewAvailability } = trpc.crew.getCrewAvailability.useQuery(Number(currentCrewMember?.id));
  const busyDays = useMemo(
    () => busyDayKeys(crewAvailability ?? [], isAvailability),
    [crewAvailability, isAvailability],
  );

  const { mutate: createTask } = trpc.task.createTask.useMutation({
    onSuccess(_response) { },
    onError: () => showAvailabilityError("We saved your request but could not notify the office. Please send them a message."),
  });

  const { mutate: createAvailability } = trpc.crew.createCrewAvailability.useMutation({
    onSuccess: () => {
      router.replace("/(app)/home");
      Toast.show({
        type: 'success',
        text1: `${isAvailability ? 'Successfully added availability' : 'Unavailability request submitted for approval'}`,
      });
    },
    // Without this the screen just sat there on a failure — no toast, no navigation, nothing to
    // tell the crew member it had not saved. They retried instead, which is how one request ends
    // up in the office six times.
    onError: (error) => showAvailabilityError(error.message),
  });

  async function onSubmit(data: z.infer<typeof addAvailabilityFormSchema>, start_date: string, end_date: string) {
    const range = resolveAvailabilityRange(data, start_date, end_date);

    const rangeError = validateAvailabilityRange(range);
    if (rangeError) {
      showAvailabilityError(rangeError);
      return;
    }

    const { from_date: startDate, to_date: endDate } = range;

    const hasClashes = mappedDates.some(date => startDate <= date && date <= endDate);

    if (hasClashes && !isAvailability) {
      setOpenClashAlert(true);
      return;
    }

    const mappedAvailability = mapCreateCrewAvailabilityForBackend(data, isEmployee, range, currentCrewMember?.id!, isAvailability);

    if (!mappedAvailability) {
      return;
    }

    createAvailability(mappedAvailability);

    if (isEmployee) {
      createTask({
        title: `New Crew ${isAvailability ? 'Availability' : 'Unavailability'} request for ${currentCrewMember?.name}`,
        description: `New Crew ${isAvailability ? 'Availability' : 'Unavailability'} request for ${currentCrewMember?.name} from ${format(startDate, 'EEE dd/MM/yyyy hh:mmaaa')} to ${format(endDate, 'EEE dd/MM/yyyy hh:mmaaa')}`,
        priority: 1,
        tags: ["crew availability"],
        due_date: startDate,
        crew_id: currentCrewMember?.id,
        state_name: "Open",
        type: "crew_availability.create",
        team_id: 2
      })
    }
  }

  return (
    <>
      <Drawer.Screen
        options={{
          title: `Add ${isAvailability ? 'Availability' : 'Unavailability'}`,
          headerStyle: {
            backgroundColor: colors.primary,
          },
          headerTintColor: colors.accent,
        }}
      />
      <AvailabilityClashAlert open={openClashAlert} onOpenChange={setOpenClashAlert} />
      <ScrollView className="flex-1 p-4">
        <AvailabilityForm form={form} isAvailability={isAvailability} onSubmit={onSubmit} busyDays={busyDays} />
      </ScrollView>
    </>
  );
}